import { DataProvider } from 'react-admin';
import { db, auth, firebase } from './firebase-app';

// ─────────────────────────────────────────────────────────────────────────────
// A-3.a (auditoría, ronda 71) — DataProvider DE LA CASA.
//
// Reemplaza a react-admin-firebase 3.2.24 (abandonado, con firebase 7
// pineado). Replica EXACTA la semántica que las páginas del BO asumían de
// aquel provider — verificada contra el censo de la ronda 70/71:
//
//  · getList trae la colección ENTERA y filtra/ordena/pagina en el navegador
//    (los comentarios de las páginas cuentan con eso; el realtime de las
//    listas es del hook propio useRealtimeUpdates, no del provider).
//  · Filtro: 'q' busca contains (case-insensitive) sobre el registro entero;
//    un campo string busca contains sobre ese campo; cualquier otro tipo es
//    igualdad estricta. (Los filtros reales del BO son igualdades por
//    uid/investmentId: contains sobre un uid único equivale a exacto.)
//  · update hace doc.update() = MERGE de Firestore a nivel de campos: los
//    parciales son seguros (learning de la bitácora; los toggles de monedas
//    y las ediciones dependen de esto). JAMÁS cambiar a set() sin merge.
//  · Meta-campos en camel, como los renombraba la config vieja
//    (metaFieldCasing camel + renameMetaFields): createdAt/updatedAt con
//    serverTimestamp, createdBy/updatedBy con el EMAIL del admin
//    (associateUsersById era false).
//  · El id se escribe TAMBIÉN como campo del doc (dontAddIdFieldToDoc era
//    false): los docs existentes lo traen y las páginas lo leen.
//  · Los Timestamp de Firestore se traducen a Date en TODAS las lecturas
//    (los 27 DateField del BO dependen de eso), recursivo en objetos y
//    arrays.
//  · softDelete era false: delete borra de verdad.
//
// Sin realtime, sin caché: cada getList lee la colección (igual que la
// configuración vieja con lazyLoading disabled). El costo es idéntico al que
// el BO ya pagaba.
// ─────────────────────────────────────────────────────────────────────────────

type AnyRecord = Record<string, any>;

// Firestore Timestamp → Date, recursivo. Duck-typing a propósito: cualquier
// objeto con toDate() (Timestamp compat o admin) se traduce.
function fromFirestore(value: any): any {
  if (value === null || value === undefined) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (Array.isArray(value)) return value.map(fromFirestore);
  if (typeof value === 'object' && value.constructor === Object) {
    const out: AnyRecord = {};
    for (const k of Object.keys(value)) out[k] = fromFirestore(value[k]);
    return out;
  }
  return value;
}

// Firestore rechaza undefined: se poda recursivo antes de escribir. null se
// conserva (es un valor válido y las páginas lo usan para "vaciar" campos).
function sanitize(value: any): any {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : sanitize(v)));
  if (typeof value === 'object' && value.constructor === Object) {
    const out: AnyRecord = {};
    for (const k of Object.keys(value)) {
      const v = sanitize(value[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return value;
}

function docToRecord(doc: firebase.firestore.DocumentSnapshot): AnyRecord {
  return { id: doc.id, ...fromFirestore(doc.data() ?? {}) };
}

async function getAll(resource: string): Promise<AnyRecord[]> {
  const snap = await db.collection(resource).get();
  return snap.docs.map(docToRecord);
}

function matchesFilter(row: AnyRecord, filter: AnyRecord): boolean {
  for (const key of Object.keys(filter ?? {})) {
    const fv = filter[key];
    if (fv === undefined || fv === null || fv === '') continue;
    if (key === 'q') {
      const q = String(fv).toLowerCase();
      if (!JSON.stringify(row).toLowerCase().includes(q)) return false;
      continue;
    }
    const rv = row[key];
    if (typeof fv === 'string') {
      if (!String(rv ?? '').toLowerCase().includes(fv.toLowerCase())) return false;
    } else if (rv !== fv) {
      return false;
    }
  }
  return true;
}

function compareValues(a: any, b: any): number {
  const an = a === undefined || a === null;
  const bn = b === undefined || b === null;
  if (an && bn) return 0;
  if (an) return 1; // nulos al final
  if (bn) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

function applySort(rows: AnyRecord[], sort?: { field: string; order: string }): AnyRecord[] {
  if (!sort?.field) return rows;
  const dir = String(sort.order).toUpperCase() === 'DESC' ? -1 : 1;
  return [...rows].sort((a, b) => dir * compareValues(a[sort.field], b[sort.field]));
}

function applyPage<T>(rows: T[], pagination?: { page: number; perPage: number }): T[] {
  if (!pagination?.perPage) return rows;
  const start = (pagination.page - 1) * pagination.perPage;
  return rows.slice(start, start + pagination.perPage);
}

function adminEmail(): string {
  return String(auth.currentUser?.email ?? '').trim().toLowerCase();
}

const sts = () => firebase.firestore.FieldValue.serverTimestamp();

let provider = {
  getList: async (resource: string, params: AnyRecord) => {
    const all = await getAll(resource);
    const filtered = all.filter((r) => matchesFilter(r, params?.filter ?? {}));
    const sorted = applySort(filtered, params?.sort);
    return { data: applyPage(sorted, params?.pagination), total: filtered.length };
  },

  getOne: async (resource: string, params: AnyRecord) => {
    const doc = await db.collection(resource).doc(String(params.id)).get();
    if (!doc.exists) throw new Error(`${resource}/${params.id} no existe`);
    return { data: docToRecord(doc) };
  },

  getMany: async (resource: string, params: AnyRecord) => {
    const docs = await Promise.all(
      (params?.ids ?? []).map((id: any) => db.collection(resource).doc(String(id)).get()),
    );
    return { data: docs.filter((d) => d.exists).map(docToRecord) };
  },

  getManyReference: async (resource: string, params: AnyRecord) => {
    const all = await getAll(resource);
    const filter = { ...(params?.filter ?? {}), [params.target]: params.id };
    const filtered = all.filter((r) => matchesFilter(r, filter));
    const sorted = applySort(filtered, params?.sort);
    return { data: applyPage(sorted, params?.pagination), total: filtered.length };
  },

  create: async (resource: string, params: AnyRecord) => {
    const data = sanitize(params?.data ?? {});
    const ref =
      data.id !== undefined && data.id !== null && data.id !== ''
        ? db.collection(resource).doc(String(data.id))
        : db.collection(resource).doc();
    const email = adminEmail();
    await ref.set({
      ...data,
      id: ref.id,
      createdAt: sts(),
      updatedAt: sts(),
      createdBy: email,
      updatedBy: email,
    });
    return { data: { ...(params?.data ?? {}), id: ref.id } };
  },

  update: async (resource: string, params: AnyRecord) => {
    const { id: _omit, ...rest } = params?.data ?? {};
    const data = sanitize(rest);
    // update() = merge a nivel de campos (parciales seguros). Ver cabecera.
    await db.collection(resource).doc(String(params.id)).update({
      ...data,
      id: String(params.id),
      updatedAt: sts(),
      updatedBy: adminEmail(),
    });
    return { data: { ...(params?.data ?? {}), id: params.id } };
  },

  updateMany: async (resource: string, params: AnyRecord) => {
    const data = sanitize(params?.data ?? {});
    const email = adminEmail();
    await Promise.all(
      (params?.ids ?? []).map((id: any) =>
        db.collection(resource).doc(String(id)).update({ ...data, updatedAt: sts(), updatedBy: email }),
      ),
    );
    return { data: params?.ids ?? [] };
  },

  delete: async (resource: string, params: AnyRecord) => {
    const previous =
      params?.previousData ??
      (await db
        .collection(resource)
        .doc(String(params.id))
        .get()
        .then((d) => (d.exists ? docToRecord(d) : { id: params.id }))
        .catch(() => ({ id: params.id })));
    await db.collection(resource).doc(String(params.id)).delete();
    return { data: previous };
  },

  deleteMany: async (resource: string, params: AnyRecord) => {
    await Promise.all(
      (params?.ids ?? []).map((id: any) => db.collection(resource).doc(String(id)).delete()),
    );
    return { data: params?.ids ?? [] };
  },
};

// El contrato de react-admin 3 es genérico agresivo; el objeto cumple la
// superficie completa (9 métodos, shapes {data}/{data,total}) y se exporta
// tipado como DataProvider para el <Admin> y los callers directos.
export let dataProvider = provider as unknown as DataProvider;
